import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { categoryApi, photoTypeApi } from '../../services/api';

const AdminCategories = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [photoTypeMap, setPhotoTypeMap] = useState({});
  const navigate = useNavigate();
  const { companySlug } = useParams();

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const [catRes, ptRes] = await Promise.all([
        categoryApi.list({ page, limit: pagination.limit }),
        photoTypeApi.all(),
      ]);
      setData(catRes.data.list || []);
      setPagination(prev => ({ ...prev, page, total: catRes.data.pagination?.total || 0 }));

      // 构建照片类型名称映射
      const map = {};
      (ptRes.data || []).forEach(pt => { map[pt.id] = pt.name; });
      setPhotoTypeMap(map);
    } catch (err) {
      message.error('加载品类失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      await categoryApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '关联照片类型',
      dataIndex: 'photo_types',
      render: (photoTypes) => {
        if (!photoTypes) return '-';
        const ids = photoTypes.split(',').map(Number).filter(n => !isNaN(n));
        return (
          <Space wrap>
            {ids.map(id => (
              <Tag key={id} color="blue">{photoTypeMap[id] || `类型${id}`}</Tag>
            ))}
          </Space>
        );
      },
    },
    { title: '排序', dataIndex: 'sort_order', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/${companySlug}/admin/categories/${record.id}/edit`)}>编辑</Button>
          <Popconfirm title="确定删除此品类？已有批次的品类名称不会被影响。" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>品类管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/${companySlug}/admin/categories/new`)}>新增品类</Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          total: pagination.total,
          pageSize: pagination.limit,
          onChange: (page) => fetchData(page),
          showTotal: (total) => `共 ${total} 条`,
        }}
      />
    </div>
  );
};

export default AdminCategories;
